import React, { memo } from 'react';
import ButtonBase from '@mui/material/ButtonBase';
import CheckBox from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlank from '@mui/icons-material/CheckBoxOutlineBlank';
import { alpha } from '@mui/material/styles';

interface CheckToggleProps {
  label: string;
  checked: boolean;
  onClick: () => void;
  /** `error` tints the whole control while checked (e.g. hazardous material). */
  tone?: 'primary' | 'error';
}

/** Compact borderless checkbox button (role="checkbox"). */
const CheckToggle: React.FC<CheckToggleProps> = ({ label, checked, onClick, tone = 'primary' }) => (
  <ButtonBase
    role="checkbox"
    aria-checked={checked}
    onClick={onClick}
    sx={(theme) => {
      const color = theme.palette[tone].main;
      const tinted = checked && tone === 'error';
      return {
        height: 32,
        pl: 0.75,
        pr: 1.25,
        gap: 0.75,
        borderRadius: 2,
        fontSize: 13,
        whiteSpace: 'nowrap',
        color: tinted ? color : 'text.primary',
        bgcolor: tinted ? alpha(color, 0.08) : 'transparent',
        '& svg': { fontSize: 20, color: checked ? color : 'text.secondary' },
        '&:hover': { bgcolor: tinted ? alpha(color, 0.14) : 'action.hover' },
        '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
      };
    }}
  >
    {checked ? <CheckBox /> : <CheckBoxOutlineBlank />}
    {label}
  </ButtonBase>
);

export default memo(CheckToggle);
